/**
 * This file provides Axis components that wrap Visx's Axis components to provide default styles for Benchling's
 * style system and conveniently interpret our internal VisxAxisConfig type.
 */
import {
  AxisBottom as VisxAxisBottom,
  AxisLeft as VisxAxisLeft,
  AxisTop as VisxAxisTop,
  SharedAxisProps,
} from '@visx/axis';
import {TextProps} from '@visx/text';
import React from 'react';

import {assertAndContinue} from 'Asserts';

import {VisxAxisConfig} from './utils/getVisxAxisConfig';
import {
  AXIS_LABEL_TO_CHART_EDGE,
  DEFAULT_AXIS_LINE_STYLE,
  DEFAULT_LABEL_STYLE,
  TICK_LABEL_TO_AXIS,
} from './utils/Theme';
import {TextAnchor, VisxAxisScale} from './VisxUtilityTypes';

/**
 * Enum built on top of @visx/axis's Orientation string literal type.
 *
 * The enum structure makes it easy to use within our codebase, and the enum keys match Visx's string literal
 * so we can easily use Visx components.
 */
export enum AxisOrientation {
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
  TOP = 'top',
}

export type XAxisOrientation = AxisOrientation.BOTTOM | AxisOrientation.TOP;

export interface BaseAxisProps {
  axisConfig: VisxAxisConfig;
  hideAxisLine?: boolean;
}

export const BASE_AXIS_PROPS: Partial<SharedAxisProps<VisxAxisScale>> = {
  ...DEFAULT_AXIS_LINE_STYLE,
  hideTicks: true,
};

const DEFAULT_LONGEST_Y_TICK_LABEL_WIDTH = 30;

const DEFAULT_AXIS_LABEL_STYLE = {
  ...DEFAULT_LABEL_STYLE,
  textAnchor: TextAnchor.MIDDLE,
};

export function getBaseVisxAxisProps({
  axisConfig: {label, scale, suggestedNumTicks, tickLabelFormatter},
  hideAxisLine = false,
}: BaseAxisProps): SharedAxisProps<VisxAxisScale> {
  return {
    ...BASE_AXIS_PROPS,
    hideAxisLine,
    label,
    numTicks: suggestedNumTicks,
    scale,
    tickFormat: tickLabelFormatter,
  };
}

/**
 * Return translation props for the axis label of bottom axes.
 *
 * The bottom axis's label is placed depending on the bottom margin, which ensures it doesn't overlap with
 * rotated tick labels.
 *
 * We return values for the `dy` and `y` props, which together control the vertical position of the axis label.
 * `dy` controls the vertical positioning of the outer `<svg>` element that `VisxAxisBottom` wraps the axis
 * label with. We set `dy` to 0 to exclusively control the vertical position of the label with the inner
 * element. `y` controls the vertical position of the inner text element.
 */
export function getBottomAxisLabelTranslation(marginBottom: number): Pick<TextProps, 'dy' | 'y'> {
  return {
    dy: 0,
    y: marginBottom - AXIS_LABEL_TO_CHART_EDGE,
  };
}

/**
 * Return translation props for the axis label of vertical axes.
 *
 * Vertical axis labels are placed depending on the longest axis tick label, which ensures it doesn't overlap
 * with any tick labels. The left margin is not used here as the left margin may be increased for long tick
 * labels on the bottom axis, which shouldn't affect the left axis label's position.
 *
 * We return values for the `dx` and `y` props, which together control the horizontal position of the axis
 * label. `dx` controls the horizontal positioning of the outer `<svg>` element that `VisxAxisLeft` /
 * `VisxAxisRight` wraps the axis label with. We set `dx` to 0 to exclusively control the horizontal position
 * of the label with the inner element. `y` controls the vertical position of the inner text element, and since
 * the inner text element is rotate 90 degrees for the y axis, "vertical" means "horizontal" when looking at the chart.
 */
export function getLeftAxisLabelTranslation(longestTickLabelWidth: number): Pick<TextProps, 'dx' | 'y'> {
  return {
    dx: 0,
    y: -(TICK_LABEL_TO_AXIS + longestTickLabelWidth + AXIS_LABEL_TO_CHART_EDGE),
  };
}

export function AxisBottom(props: BaseAxisProps & {chartHeight: number; marginBottom: number}): JSX.Element {
  const {
    axisConfig: {tickLabelRotation},
    chartHeight,
    marginBottom,
  } = props;

  return (
    <VisxAxisBottom
      {...getBaseVisxAxisProps(props)}
      labelProps={{
        ...DEFAULT_AXIS_LABEL_STYLE,
        ...getBottomAxisLabelTranslation(marginBottom),
      }}
      top={chartHeight}
      tickLabelProps={() => ({
        ...DEFAULT_AXIS_LABEL_STYLE,
        textAnchor: TextAnchor.MIDDLE,
        ...(tickLabelRotation != null && tickLabelRotation !== 0
          ? {
              angle: -1 * tickLabelRotation,
              textAnchor: TextAnchor.END,
            }
          : {}),
      })}
    />
  );
}

export function AxisLeft(
  props: BaseAxisProps & {
    longestYTickLabelWidth?: number;
  }
): JSX.Element {
  const {axisConfig, longestYTickLabelWidth} = props;
  assertAndContinue(
    axisConfig.label == null || (axisConfig.label != null && longestYTickLabelWidth != null),
    'Expected longestYTickLabelWidth to be specified when axis has a label.'
  );

  return (
    <VisxAxisLeft
      {...getBaseVisxAxisProps(props)}
      {...(props.axisConfig.label !== undefined
        ? {
            labelProps: {
              ...DEFAULT_AXIS_LABEL_STYLE,
              ...getLeftAxisLabelTranslation(
                props.longestYTickLabelWidth || DEFAULT_LONGEST_Y_TICK_LABEL_WIDTH
              ),
            },
          }
        : {})}
      tickLabelProps={() => ({
        ...DEFAULT_AXIS_LABEL_STYLE,
        dx: -TICK_LABEL_TO_AXIS,
        textAnchor: TextAnchor.END,
      })}
    />
  );
}

export function AxisTop(props: BaseAxisProps): JSX.Element {
  const {
    axisConfig: {tickLabelRotation},
  } = props;

  return (
    <VisxAxisTop
      {...getBaseVisxAxisProps(props)}
      tickLabelProps={() => ({
        ...DEFAULT_LABEL_STYLE,
        textAnchor: TextAnchor.MIDDLE,
        ...(tickLabelRotation != null && tickLabelRotation !== 0
          ? {
              angle: tickLabelRotation,
              textAnchor: TextAnchor.START,
            }
          : {}),
      })}
    />
  );
}
